/**
 * Copyright (c) 2017-2020 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author David Sehnal <david.sehnal@gmail.com>
 * @author Alexander Rose <alexander.rose@weirdbyte.de>
 */
import { mmCIF_Schema } from '../../../mol-io/reader/cif/schema/mmcif';
import { Spacegroup, SpacegroupCell, SymmetryOperator } from '../../../mol-math/geometry';
import { Tensor, Vec3, Mat3 } from '../../../mol-math/linear-algebra';
import { createAssemblies } from './assembly';
import { FormatPropertyProvider } from '../common/property';
export { ModelSymmetry };
var ModelSymmetry;
(function (ModelSymmetry) {
    ModelSymmetry.Descriptor = {
        name: 'model_symmetry',
    };
    ModelSymmetry.Provider = FormatPropertyProvider.create(ModelSymmetry.Descriptor);
    function fromData(data) {
        var assemblies = createAssemblies(data.pdbx_struct_assembly, data.pdbx_struct_assembly_gen, data.pdbx_struct_oper_list);
        var spacegroup = getSpacegroup(data.symmetry, data.cell);
        var isNonStandardCrystalFrame = checkNonStandardCrystalFrame(data.atom_sites, spacegroup);
        return { assemblies: assemblies, spacegroup: spacegroup, isNonStandardCrystalFrame: isNonStandardCrystalFrame, ncsOperators: getNcsOperators(data.struct_ncs_oper) };
    }
    ModelSymmetry.fromData = fromData;
    function fromCell(size, anglesInRadians) {
        var spaceCell = SpacegroupCell.create('P 1', size, anglesInRadians);
        var spacegroup = Spacegroup.create(spaceCell);
        return { assemblies: [], spacegroup: spacegroup, isNonStandardCrystalFrame: false };
    }
    ModelSymmetry.fromCell = fromCell;
})(ModelSymmetry || (ModelSymmetry = {}));
function checkNonStandardCrystalFrame(atom_sites, spacegroup) {
    if (atom_sites._rowCount === 0)
        return false;
    // TODO: parse atom_sites transform and check if it corresponds to the toFractional matrix
    return false;
}
function getSpacegroupNameOrNumber(symmetry) {
    var groupNumber = symmetry['Int_Tables_number'].value(0);
    var groupName = symmetry['space_group_name_H-M'].value(0);
    if (!symmetry['Int_Tables_number'].isDefined)
        return groupName;
    if (!symmetry['space_group_name_H-M'].isDefined)
        return groupNumber;
    return groupName;
}
function getSpacegroup(symmetry, cell) {
    if (symmetry._rowCount === 0 || cell._rowCount === 0)
        return Spacegroup.ZeroP1;
    var nameOrNumber = getSpacegroupNameOrNumber(symmetry);
    var spaceCell = SpacegroupCell.create(nameOrNumber, Vec3.create(cell.length_a.value(0), cell.length_b.value(0), cell.length_c.value(0)), Vec3.scale(Vec3.zero(), Vec3.create(cell.angle_alpha.value(0), cell.angle_beta.value(0), cell.angle_gamma.value(0)), Math.PI / 180));
    return Spacegroup.create(spaceCell);
}
function getNcsOperators(struct_ncs_oper) {
    if (struct_ncs_oper._rowCount === 0)
        return void 0;
    var id = struct_ncs_oper.id, matrix = struct_ncs_oper.matrix, vector = struct_ncs_oper.vector;
    var matrixSpace = mmCIF_Schema.struct_ncs_oper.matrix.space, vectorSpace = mmCIF_Schema.struct_ncs_oper.vector.space;
    var opers = [];
    for (var i = 0; i < struct_ncs_oper._rowCount; i++) {
        var m = Tensor.toMat3(Mat3(), matrixSpace, matrix.value(i));
        var v = Tensor.toVec3(Vec3(), vectorSpace, vector.value(i));
        if (!SymmetryOperator.checkIfRotationAndTranslation(m, v))
            continue;
        // ignore non-identity 'given' NCS operators
        if (struct_ncs_oper.code.value(i) === 'given' && !Mat3.isIdentity(m) && !Vec3.isZero(v))
            continue;
        var ncsId = id.value(i);
        opers[opers.length] = SymmetryOperator.ofRotationAndOffset("ncs_" + ncsId, m, v, ncsId);
    }
    return opers;
}
//# sourceMappingURL=symmetry.js.map